import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchManagedPlacements } from '../../hooks/useFetchManagedPlacements';
import { useSiteAdsManaged } from '../../hooks/useSiteAdsManaged';
import { trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst } from '../../store/selectors/adSelectors';
import { managedPlacementToAd, pickStaticFallback } from '../../utils/adMapper';
import { selectAdsStaticEnabled } from '../../store/reducers/sitePublicReducer';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';
import StaticAdBanner from './StaticAdBanner';

const INLINE_REQ = Object.freeze([{ placement: 'inline-banner', limit: 1 }]);

/**
 * Fetches `inline-banner` for `pageKey`, merges with `fallbackAd`, tracks impression/click when managed.
 */
export default function ManagedInlineBanner({ pageKey, fallbackAd, variant = 'inline' }) {
  const dispatch = useDispatch();
  const { sitePublicLoaded, adsManagedEnabled } = useSiteAdsManaged();
  const adsStaticEnabled = useSelector(selectAdsStaticEnabled);
  const { adDomain, adLocale, adDevice } = useFetchManagedPlacements(pageKey, INLINE_REQ);
  const managed = useSelector((s) => selectAdPlacementFirst(s, pageKey, 'inline-banner'));
  const ad = useMemo(
    () => managedPlacementToAd(managed, pickStaticFallback(adsStaticEnabled, fallbackAd)),
    [managed, fallbackAd, adsStaticEnabled],
  );

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managed?._id) return;
    const key = makeImpressionKey({
      campaign: managed._id,
      page: pageKey,
      placement: 'inline-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managed._id,
          page: pageKey,
          placement: 'inline-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managed, pageKey, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onLinkClick = useCallback(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managed?._id) return;
    dispatch(
      trackAdClick({
        campaign: managed._id,
        page: pageKey,
        placement: 'inline-banner',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managed, pageKey, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  return <StaticAdBanner ad={ad} variant={variant} pageKey={pageKey} onLinkClick={onLinkClick} />;
}
